import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { mayaCustomerInitiativeListRoute } from 'basics/constants/routes.constants';
import { CiStatus } from 'basics/enums/maya.enums';
import { useCreateCustomerInitiativeMutation } from 'basics/graphql/mutations/createCustomerInitiative';
import useGetCustomerInitiativeByIdQuery from 'basics/graphql/queries/getCustomerInitiativeById';
import { succesToast, warningToast } from 'basics/utils/toast';
import {
  CiFormCanceltCallbackType,
  CiFormSubmitCallbackType,
} from 'components/CustomerInitiativeForm/CustomerInitiativeForm.types';
import { CustomerInitiative } from 'generated/maya.types';

const useCustomerInitiativeDuplicate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [createCustomerInitiative] = useCreateCustomerInitiativeMutation();

  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);

  const [loading, setLoading] = useState(true);

  const customerInitiative = useGetCustomerInitiativeByIdQuery(urlParams.get('cepSourceId')).data;
  const prefix = customerInitiative?.isTemplate ? 'From' : 'Copy';
  const duplicatedCustomerInitiative = {
    ...customerInitiative,
    status: CiStatus.DRAFT,
    title: `${prefix} - ${customerInitiative?.title}`,
    solutionCoreStory: t('maya_field_is_hidden_default_value'),
  } as CustomerInitiative;

  useEffect(() => {
    if (customerInitiative && loading) {
      setLoading(false);
    }
  }, [customerInitiative, loading, setLoading]);

  const handleCancelCustomerInitiativeForm: CiFormCanceltCallbackType = () => {
    navigate(`${mayaCustomerInitiativeListRoute}?year=${customerInitiative?.year}`);
  };

  const handleSaveCustomerInitiativeForm: CiFormSubmitCallbackType = async (ci) => {
    const { data: customerInitiativeData, error } = await createCustomerInitiative({ ...ci, isTemplate: false });
    const year = new Date(ci.dateStart).getFullYear();

    if (customerInitiativeData && !error) {
      succesToast(t('maya_ci_created', { title: ci.title }));
      navigate(`${mayaCustomerInitiativeListRoute}?year=${year}`);
    } else {
      warningToast(error || t('errors_standard'));
      navigate(`${mayaCustomerInitiativeListRoute}?year=${year}`);
    }
  };

  return {
    loading,
    customerInitiative: duplicatedCustomerInitiative,
    handleCancelCustomerInitiativeForm,
    handleSaveCustomerInitiativeForm,
  };
};

export default useCustomerInitiativeDuplicate;
